import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import "./CSS/Product.css";
import { API_BASE, FILE_BASE } from "../config";

const ProductGrid = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // =========================================================
  // RESPONSIVE PAGE SIZE
  // =========================================================
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= 768
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const itemsPerPage = isMobile ? 15 : 16;

  // =========================================================
  // SAFE VALUE
  // =========================================================
  const getSafeValue = (value, fallback = "") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (
      typeof value === "string" ||
      typeof value === "number"
    ) {
      return value;
    }

    if (typeof value === "object") {
      return (
        value.name ||
        value.title ||
        value.productName ||
        value.value ||
        value.text ||
        fallback
      );
    }

    return fallback;
  };

  // =========================================================
  // GET PRODUCT ID
  // =========================================================
  const getProductId = (item) => {
    return (
      item?.id ||
      item?.productId ||
      item?.product?.id ||
      null
    );
  };

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(
          `${API_BASE}/Products?page=${currentPage}&pageSize=${itemsPerPage}`
        );

        console.log(
          "Products API Response:",
          response.data
        );

        const firstProduct = Array.isArray(response.data)
          ? response.data[0]
          : response.data?.products?.[0] ||
            response.data?.items?.[0];

        console.log(
          "FIRST PRODUCT:",
          JSON.stringify(firstProduct, null, 2)
        );

        // ARRAY
        if (Array.isArray(response.data)) {
          setProducts(response.data);
        }

        // { products: [] }
        else if (
          Array.isArray(response.data?.products)
        ) {
          setProducts(response.data.products);
        }

        // { items: [] }
        else if (
          Array.isArray(response.data?.items)
        ) {
          setProducts(response.data.items);
        }

        // EMPTY
        else {
          setProducts([]);
        }
      } catch (err) {
        console.error(
          "Error fetching products:",
          err
        );

        setProducts([]);
        setError("Unable to load products.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [currentPage, itemsPerPage]);

  // =========================================================
  // DISCOUNT
  // =========================================================
  const calculateDiscount = (
    originalPrice,
    finalPrice
  ) => {
    const original = Number(
      getSafeValue(originalPrice, 0)
    );

    const final = Number(
      getSafeValue(finalPrice, 0)
    );

    if (
      !isNaN(original) &&
      !isNaN(final) &&
      original > final &&
      original > 0
    ) {
      return Math.round(
        ((original - final) / original) * 100
      );
    }

    return 0;
  };

  // =========================================================
  // PRODUCT IMAGE
  // =========================================================
  const getProductImage = (image) => {
    const fallback =
      "https://dummyimage.com/400x400/e5e5e5/000000&text=No+Image";

    if (!image) {
      return fallback;
    }

    // IMAGE OBJECT
    if (typeof image === "object") {
      image =
        image.imageUrl ||
        image.url ||
        image.path ||
        image.src ||
        image.image ||
        "";
    }

    if (
      typeof image !== "string" ||
      !image
    ) {
      return fallback;
    }

    // HTTPS
    if (image.startsWith("https://")) {
      return image;
    }

    // HTTP → HTTPS
    if (image.startsWith("http://")) {
      return image.replace(
        /^http:\/\//i,
        "https://"
      );
    }

    // RELATIVE IMAGE
    return `${FILE_BASE}${
      image.startsWith("/") ? "" : "/"
    }${image}`;
  };

  // =========================================================
  // VIEW PRODUCT
  // =========================================================
  const handleProductClick = (item) => {
    const productId = getProductId(item);

    if (!productId) {
      console.error(
        "Product ID missing:",
        item
      );

      return;
    }

    console.log(
      "Navigating to Product:",
      productId
    );

    navigate(`/product/${productId}`);
  };

  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="product-grid-wrapper">

      {/* PRODUCT GRID */}
      <div className="product-grid">

        {/* LOADING */}
        {loading && (
          <div className="no-products">
            <p>Loading products...</p>
          </div>
        )}

        {/* ERROR */}
        {!loading && error && (
          <div className="no-products">
            <p>{error}</p>
          </div>
        )}

        {/* NO PRODUCTS */}
        {!loading &&
          !error &&
          products.length === 0 && (
            <div className="no-products">
              <p>No products found.</p>
            </div>
          )}

        {/* PRODUCTS */}
        {!loading &&
          !error &&
          products.length > 0 &&
          products.map((item) => {

            const productId =
              getProductId(item);

            const discount =
              calculateDiscount(
                item?.originalPrice,
                item?.finalPrice
              );

            const productName =
              getSafeValue(
                item?.product,
                "Product"
              );

            const finalPrice =
              getSafeValue(
                item?.finalPrice,
                "0"
              );

            const originalPrice =
              getSafeValue(
                item?.originalPrice,
                "0"
              );

            return (
              <div
                className="product-card"
                key={productId || Math.random()}
              >

                {/* IMAGE */}
                <div className="product-image-box">

                  <img
                    src={getProductImage(
                      item?.thumbnail
                    )}
                    alt={productName}
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://dummyimage.com/400x400/e5e5e5/000000&text=No+Image";
                    }}
                  />

                  {/* DISCOUNT */}
                  {discount > 0 && (
                    <span className="badge sale">
                      -{discount}%
                    </span>
                  )}

                  {/* OVERLAY */}
                  <div className="overlays">

                    <button
                      type="button"
                      className="add-cart-btns"
                      onClick={() =>
                        handleProductClick(item)
                      }
                    >

                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M7 22C6.45 22 5.97917 21.8042 5.5875 21.4125C5.19583 21.0208 5 20.55 5 20C5 19.45 5.19583 18.9792 5.5875 18.5875C5.97917 18.1958 6.45 18 7 18C7.55 18 8.02083 18.1958 8.4125 18.5875C8.80417 18.9792 9 19.45 9 20C9 20.55 8.80417 20.9792 8.4125 21.4125C8.02083 21.8042 7.55 22 7 22ZM17 22C16.45 22 15.9792 21.8042 15.5875 21.4125C15.19583 21.0208 15 20.55 15 20C15 19.45 15.1958 18.9792 15.5875 18.5875C15.9792 18.1958 16.45 18 17 18C17.55 18 18.0208 18.1958 18.4125 18.5875C18.8042 18.9792 19 20.55 18.4125 21.4125C18.0208 21.8042 17.55 22 17 22ZM6.15 6L8.55 11H15.55L18.3 6H6.15ZM5.2 4H19.95C20.3333 4 20.625 4.17083 20.825 4.5125C21.025 4.85417 21.0333 5.2 20.85 5.55L17.3 11.95C17.1167 12.2833 16.8708 12.5417 16.5625 12.725C16.2542 12.9083 15.9167 13 15.55 13H8.1L7 15H19V17H7C6.25 17 5.68333 16.6708 5.3 16.0125C4.91667 15.3542 4.9 14.7 5.25 14.05L6.6 11.6L3 4H1V2H4.25L5.2 4Z"
                          fill="white"
                        />
                      </svg>

                      View Product

                    </button>

                  </div>

                </div>

                {/* CONTENT */}
                <div className="product-card-content">

                  <h3>
                    {productName}
                  </h3>

                  <div className="product-price">

                    <span className="product-new-price">
                      Rs {finalPrice}
                    </span>

                    {Number(originalPrice) >
                      Number(finalPrice) && (
                      <span className="product-old-price">
                        Rs {originalPrice}
                      </span>
                    )}

                  </div>

                </div>

              </div>
            );
          })}

      </div>

      {/* PAGINATION */}
      {!loading &&
        !error &&
        products.length > 0 && (
          <div className="pagination">

            <button
              disabled={currentPage === 1}
              onClick={() =>
                setCurrentPage(
                  (prev) =>
                    Math.max(prev - 1, 1)
                )
              }
            >
              Prev
            </button>

            <button className="active">
              {currentPage}
            </button>

            <button
              className="next"
              onClick={() =>
                setCurrentPage(
                  (prev) => prev + 1
                )
              }
            >
              Next
            </button>

          </div>
        )}

    </div>
  );
};

export default ProductGrid;